import { Controller } from 'stimulus';
import 'lib/vendor/snowplow';
import { getCurrentUserState, getCurrentPageParam, featureEnabled } from 'lib/helpers';
import events from 'lib/events';

export default class SnowplowAnalyticsController extends Controller {
  connect(collector, app, trackResourceImpressions) {
    this.fireResourceImpressionsEvent = this.fireResourceImpressionsEvent.bind(this);
    this.sp = window.snowplow;
    this.trackResourceImpressions = trackResourceImpressions;

    this.sp('newTracker', 'cf', collector, {
      appId: app,
      discoverRootDomain: true,
      eventMethod: 'beacon',
      contexts: {
        webPage: true,
        performanceTiming: true,
        gaCookies: true,
        geolocation: false,
      },
    });
    this.sp('setUserIdFromCookie', 'session_guid');
    this.sp('enableActivityTracking', 10, 10);
    this.sp('enableLinkClickTracking');
    this.sp('trackPageView', null, [], this.getCustomContexts);

    if (this.trackResourceImpressions) window.addEventListener('turbolinks:load', this.fireResourceImpressionsEvent);
    window.addEventListener(events.snowplow.webVitalsReport, this.trackWebVitalsReport);
    window.addEventListener('turbolinks:load', this.collectTurbolinksTiming);
  }

  disconnect() {
    window.removeEventListener('turbolinks:load', this.fireResourceImpressionsEvent);
    window.removeEventListener(events.snowplow.webVitalsReport, this.trackWebVitalsReport);
    window.removeEventListener('turbolinks:load', this.collectTurbolinksTiming);
  }

  getCustomContexts() {
    const userState = getCurrentUserState();

    return [{
      schema: 'iglu:com.eezy/user_state/jsonschema/1-0-1',
      data: userState,
    }];
  }

  getResourceImpressionsData() {
    const data = {
      ids: [],
      pros: [],
    };

    document.querySelectorAll('[data-item-id]').forEach((resourceEl) => {
      data.ids.push(parseInt(resourceEl.getAttribute('data-item-id'), 10));

      // use {0,1} instead of booleans to save space in the JSON
      if (resourceEl.getAttribute('data-pro') === 'true') {
        data.pros.push(1);
      } else {
        data.pros.push(0);
      }
    });

    const page = getCurrentPageParam();
    if (page) data.page = parseInt(page, 10);

    return data;
  }

  fireResourceImpressionsEvent() {
    const data = this.getResourceImpressionsData();

    if (data.ids.length > 0) {
      this.sp('trackSelfDescribingEvent', {
        schema: 'iglu:com.eezy/resource_impressions/jsonschema/1-0-0',
        data,
      },
      this.getCustomContexts());
    }
  }

  trackWebVitalsReport = (event) => {
    if (!featureEnabled('web-vitals-snowplow')) return false;
    if (window.EezyTurbolinksVisits > 0) return false;
    if (event.detail.isFinal === false && event.detail.name !== 'CLS') return false;
    if (event.detail.name === 'CLS' && event.detail.value === 0) return false;

    const transformedWebVitalsReport = this.transformWebVitalsReport(event.detail);

    this.sp('trackSelfDescribingEvent', {
      schema: 'iglu:com.eezy/web_vitals/jsonschema/1-0-1',
      data: transformedWebVitalsReport,
    },
    this.getCustomContexts());

    return true;
  }

  transformWebVitalsReport(eventDetail) {
    const transformedWebVitalsReport = {};

    transformedWebVitalsReport.name = eventDetail.name;
    transformedWebVitalsReport.val = (transformedWebVitalsReport.name !== 'CLS')
      ? Math.trunc(eventDetail.value)
      : this.shiftCLSvalue(eventDetail.value);
    transformedWebVitalsReport.entries = [];

    switch (transformedWebVitalsReport.name) {
      case 'LCP':
        eventDetail.entries.forEach((entry) => {
          const newEntry = {};

          newEntry.val = Math.trunc(entry.size);
          newEntry.url = entry.url.substring(0, 255);
          newEntry.el = this.nodeElementToString(entry.element);
          newEntry.load = Math.trunc(entry.loadTime);
          newEntry.sTime = Math.trunc(entry.startTime);
          newEntry.render = Math.trunc(entry.renderTime);

          transformedWebVitalsReport.entries.push(newEntry);
        });
        break;

      case 'FCP':
        eventDetail.entries.forEach((entry) => {
          const newEntry = {};

          newEntry.sTime = Math.trunc(entry.startTime);

          transformedWebVitalsReport.entries.push(newEntry);
        });
        break;

      case 'FID':
        eventDetail.entries.forEach((entry) => {
          const newEntry = {};

          newEntry.name = entry.name;
          newEntry.dur = Math.trunc(entry.duration);
          newEntry.sTime = Math.trunc(entry.startTime);
          newEntry.sProcess = Math.trunc(entry.processingStart);
          newEntry.eProcess = Math.trunc(entry.processingEnd);

          transformedWebVitalsReport.entries.push(newEntry);
        });
        break;

      case 'CLS':
        eventDetail.entries.forEach((entry) => {
          const newEntry = {};

          newEntry.dur = Math.trunc(entry.duration);
          newEntry.sTime = Math.trunc(entry.startTime);
          newEntry.recentIn = entry.hadRecentInput;
          newEntry.lastIn = Math.trunc(entry.lastInputTime);
          newEntry.val = this.shiftCLSvalue(entry.value);

          // Collect element names from the CLS event
          newEntry.els = [];
          if (featureEnabled('web-vitals-cls-sources')) {
            newEntry.els = this.collectCLSSources(entry);
          }

          transformedWebVitalsReport.entries.push(newEntry);
        });
        break;

      default:
        // Do nothing, event not supported... yet
        break;
    }

    return transformedWebVitalsReport;
  }

  nodeElementToString(element) {
    if (!element) return '';

    let tagString = `${element.tagName}`;
    if (element.classList.length > 0) tagString += `.${element.classList.toString()}`;

    return tagString.substring(0, 255);
  }

  shiftCLSvalue(clsScore) {
    return Math.trunc(clsScore * 100);
  }

  collectCLSSources(entry) {
    const sourceNodes = [];
    entry.sources.forEach((source) => {
      sourceNodes.push(this.nodeElementToString(source.node));
    });
    return sourceNodes;
  }

  collectTurbolinksTiming = (event) => {
    if (window.EezyTurbolinksVisits === 0) return false;

    const turbolinksTimingEvent = {
      visitDur: event.data.timing.visitEnd - event.data.timing.visitStart,
      reqDur: event.data.timing.requestEnd - event.data.timing.requestStart,
      prevUrl: window.EezyTurbolinksPreviousUrl,
      visitNo: window.EezyTurbolinksVisits,
    };

    this.sp('trackSelfDescribingEvent', {
      schema: 'iglu:com.eezy/turbolinks_timing/jsonschema/1-0-0',
      data: turbolinksTimingEvent,
    },
    this.getCustomContexts());

    return true;
  }
}
